import { useTranslation } from "react-i18next";
import { usePostHog } from "@/analytics/usePostHog";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@docio/shared";
import { cn } from "@/lib/utils";

interface AppStoreBadgeProps {
  /** Where on the page the badge sits — recorded as `cta_clicked.location` in PostHog. */
  location: "hero" | "final_cta" | "footer";
  /** Show the "Get the Docio app" line above the badge. */
  withPrompt?: boolean;
  className?: string;
}

/**
 * Official store badges linking to the native Docio listings.
 * The artwork is Apple's unmodified SVG; per the App Store marketing guidelines it must not be
 * recoloured, restyled or shown smaller than 40px tall.
 */
export const AppStoreBadge = ({ location, withPrompt = false, className }: AppStoreBadgeProps) => {
  const { t } = useTranslation("common");
  const posthog = usePostHog();

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      {withPrompt && (
        <p className="text-sm text-muted-foreground">{t("appStore.prompt")}</p>
      )}
      <div className={cn("flex w-full flex-wrap items-center gap-x-2", location === "footer" ? "justify-start" : "justify-center")}>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => posthog?.capture("cta_clicked", { location, cta_type: "app_store" })}
          className="inline-block shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <img
            src="/assets/app-store-badge.svg"
            alt={t("appStore.badgeAlt")}
            className="h-[40px] w-auto"
            width={120}
            height={40}
            loading={location === "hero" ? "eager" : "lazy"}
            decoding="async"
          />
        </a>
        <a
          href={GOOGLE_PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => posthog?.capture("cta_clicked", { location, cta_type: "google_play" })}
          className="inline-block shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {/* Google's artwork includes 10px of clear space at this size. Offset it in the
              footer so the visible badge aligns with Apple when the links wrap. */}
          <img
            src="/assets/google-play-badge-155.webp"
            srcSet="/assets/google-play-badge-155.webp 1x, /assets/google-play-badge-310.webp 2x"
            alt={t("appStore.googlePlayBadgeAlt")}
            className={cn("h-[60px] w-[155px] max-w-none", location === "footer" && "-ml-2.5")}
            width={155}
            height={60}
            loading={location === "hero" ? "eager" : "lazy"}
            decoding="async"
          />
        </a>
      </div>
    </div>
  );
};
